import React, { useState, useEffect } from "react";
import { AIReport } from "../../AdminServices/AdminReportService";
import { GetAllUsers } from "../../AdminServices/AdminReportService";
import { Loader2, FileText } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Select from "react-select";

const AdminAIReport = () => {
	const [users, setUsers] = useState([]);
	const [userUID, setUserUID] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [taskSummary, setTaskSummary] = useState("");
	const [loading, setLoading] = useState(false);
	const [loadingUsers, setLoadingUsers] = useState(false);
	const [error, setError] = useState("");

	// ✅ Fetch Users using service
	useEffect(() => {
		const fetchUsers = async () => {
			try {
				setLoadingUsers(true);

				const res = await GetAllUsers();

				setUsers(res.data.data || res.data.Data || []);
			} catch (err) {
				console.error("Failed to fetch users", err);
				setError("Unable to load users");
			} finally {
				setLoadingUsers(false);
			}
		};

		fetchUsers();
	}, []);

	// ✅ Generate Report
	const handleSubmit = async () => {
		if (!userUID || !startDate || !endDate) {
			setError("Please fill all fields");
			return;
		}

		if (endDate < startDate) {
			setError("End date cannot be before start date");
			return;
		}

		try {
			setLoading(true);
			setError("");
			setTaskSummary("");

			const payload = {
				userUID,
				startDate,
				endDate,
			};

			const res = await AIReport(payload);
			setTaskSummary(res.data.taskSummary);
		} catch (err) {
			console.error(err);
			setError("Failed to generate report");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen p-6">
			{/* Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-semibold text-gray-800">
					Orbit Task Report
				</h1>
				<p className="text-sm text-gray-500">
					AI-powered analysis of tasks created and managed within Orbit
				</p>
			</div>

			{/* Form */}
			<div className="bg-white p-6 rounded-2xl shadow-md mb-6 grid md:grid-cols-4 gap-4 items-end">
				{/* User Dropdown */}
				<div>
					<label className="text-sm text-gray-600">User</label>

					<Select
						options={users.map((user) => ({
							value: user.userUID,
							label: `${user.name}`,
						}))}
						value={
							users
								.map((user) => ({
									value: user.userUID,
									label: `${user.name}`,
								}))
								.find((u) => u.value === userUID) || null
						}
						onChange={(selected) => setUserUID(selected?.value || "")}
						isLoading={loadingUsers}
						placeholder="Search or select user..."
						className="mt-1"
						classNamePrefix="react-select"
					/>
				</div>

				{/* Start Date */}
				<div>
					<label className="text-sm text-gray-600">Start Date</label>
					<input
						type="date"
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						className="w-full mt-1 border px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
					/>
				</div>

				{/* End Date */}
				<div>
					<label className="text-sm text-gray-600">End Date</label>
					<input
						type="date"
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						className="w-full mt-1 border px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
					/>
				</div>

				{/* Button */}
				<button
					onClick={handleSubmit}
					disabled={loading}
					className={`h-[42px] rounded-lg text-white font-medium flex items-center justify-center gap-2 transition ${
						loading
							? "bg-gray-400 cursor-not-allowed"
							: "bg-blue-600 hover:bg-blue-700"
					}`}
				>
					{loading ? (
						<>
							<Loader2 className="animate-spin" size={16} />
							Generating...
						</>
					) : (
						"Generate Report"
					)}
				</button>
			</div>

			{/* Error */}
			{error && (
				<div className="mb-4 text-red-500 text-sm font-medium">{error}</div>
			)}

			{/* Result */}
			<div className="bg-white rounded-2xl shadow-md border">
				{!taskSummary && !loading && (
					<div className="flex flex-col items-center justify-center text-gray-400 py-16">
						<FileText size={40} className="mb-2" />
						<p>No report generated yet</p>
					</div>
				)}

				{loading && (
					<div className="p-8 space-y-8 animate-pulse">
						{/* Section 1 */}
						<div>
							<div className="h-6 w-1/3 bg-gray-300 rounded mb-4"></div>
							<div className="space-y-3">
								<div className="h-4 w-full bg-gray-200 rounded"></div>
								<div className="h-4 w-5/6 bg-gray-200 rounded"></div>
								<div className="h-4 w-4/6 bg-gray-200 rounded"></div>
							</div>
						</div>

						{/* Divider */}
						<div className="h-px bg-gray-200"></div>

						{/* Section 2 */}
						<div>
							<div className="h-5 w-1/4 bg-gray-300 rounded mb-4"></div>

							<div className="space-y-4">
								<div className="space-y-2">
									<div className="h-4 w-full bg-gray-200 rounded"></div>
									<div className="h-4 w-5/6 bg-gray-200 rounded"></div>
								</div>

								<div className="space-y-2">
									<div className="h-4 w-full bg-gray-200 rounded"></div>
									<div className="h-4 w-4/6 bg-gray-200 rounded"></div>
								</div>
							</div>
						</div>

						{/* Divider */}
						<div className="h-px bg-gray-200"></div>

						{/* Section 3 */}
						<div>
							<div className="h-5 w-1/3 bg-gray-300 rounded mb-4"></div>
							<div className="space-y-3">
								<div className="h-4 w-full bg-gray-200 rounded"></div>
								<div className="h-4 w-3/4 bg-gray-200 rounded"></div>
							</div>
						</div>
					</div>
				)}

				{taskSummary && !loading && (
					<div className="p-8">
						<div className="prose max-w-none prose-slate prose-sm md:prose-base">
							<ReactMarkdown remarkPlugins={[remarkGfm]}>
								{taskSummary}
							</ReactMarkdown>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default AdminAIReport;
